import moment from 'moment';
import { Patient } from '@/models/patient';
import { GENDER_KEY, GENDER_NAME } from '@/common/enums/gender';

/**
 * Gets the patient's full name.
 * @param patient The patient object.
 * @returns The full name or an empty string.
 */
export const getPatientFullName = (patient: Patient | null): string => {
  if (!patient) { return ''; }
  const fullName = patient.fullName?.trim();
  return fullName || '';
};

/**
 * Gets the patient's kana name (Family + Given).
 * @param patient The patient object.
 * @returns The kana name or an empty string.
 */
export const getPatientKanaName = (patient: Patient | null): string => {
  if (!patient) { return ''; }
  const familyKana = patient.family_name_kana;
  const givenKana = patient.given_name_kana;
  if (!familyKana && !givenKana) { return ''; }
  return `${familyKana || ''} ${givenKana || ''}`.trim();
};

/**
 * Gets the formatted birthday display string with age.
 * @param patient The patient object.
 * @returns Formatted string or an empty string.
 */
export const getPatientBirthdayDisplay = (patient: Patient | null): string => {
  if (!patient?.date_of_birth) { return ''; }
  const dob = moment(patient.date_of_birth).utcOffset(9);
  if (!dob.isValid()) { return ''; }
  const age = moment().utcOffset(9).diff(dob, 'years');
  return `${dob.format('YYYY年MM月DD日')} (${age}歳)`;
};

/**
 * Gets the patient's gender for display (No.173 Q7 — the client asked for
 * 性別 alongside the existing four fields).
 *
 * `gender_id` is the authority when present: it is the explicit enum the v2
 * screens use, and an absent value is distinguishable from a real one. The
 * boolean `gender` is the fallback because two existing screens
 * (PatientDetailBasicInfo, PatientDetailAssignDoctor) already render it that
 * way — but it defaults to `false`, so on its own it cannot tell "female"
 * apart from "not recorded", which is why it is not consulted first.
 * @param patient The patient object.
 * @returns '男性' / '女性', or an empty string when nothing is recorded.
 */
export const getPatientGenderDisplay = (patient: Patient | null): string => {
  if (!patient) { return ''; }
  // The chat screen's user can carry gender_id as a number, so compare as strings.
  const genderId = String(patient.gender_id);
  if (genderId === GENDER_KEY.MALE) { return GENDER_NAME.MALE; }
  if (genderId === GENDER_KEY.FEMALE) { return GENDER_NAME.FEMALE; }
  if (typeof patient.gender === 'boolean') {
    return patient.gender ? GENDER_NAME.MALE : GENDER_NAME.FEMALE;
  }
  return '';
};

/**
 * Finds the value of an answer based on a keyword in the question.
 * @param answers The list of patient answers.
 * @param keyword The keyword to search for.
 * @returns The answer value or an empty string.
 */
export const findAnswerValue = (answers: any[], keyword: string): string => {
  if (!answers || answers.length === 0) { return ''; }
  for (const data of answers) {
    const questionText = data.question?.question || '';
    if (questionText.includes(keyword)) {
      const answersList = data.answer || [];
      if (answersList.length > 0 && answersList[0].value) {
        return answersList[0].value;
      }
    }
  }
  return '';
};

/**
 * Gets the BMI display value.
 * @param answers The list of patient answers.
 * @param treatmentName The name of the treatment service.
 * @returns The BMI rounded to 1 decimal place or an empty string.
 */
export const getBMIDisplay = (answers: any[], treatmentName: string | undefined): string => {
  const isMedialDiet = treatmentName?.includes('メディカルダイエット');
  let heightValue = findAnswerValue(answers, '身長');
  let weightValue = findAnswerValue(answers, '体重');

  // For メディカルダイエット, also try broader keyword search
  if (isMedialDiet) {
    if (!heightValue) { heightValue = findAnswerValue(answers, 'cm'); }
    if (!weightValue) { weightValue = findAnswerValue(answers, 'kg'); }
  }

  if (!heightValue || !weightValue) { return ''; }

  const heightCm = parseFloat(heightValue);
  const weightKg = parseFloat(weightValue);
  if (!heightCm || !weightKg || heightCm <= 0 || weightKg <= 0) { return ''; }

  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);
  return bmi.toFixed(1);
};
